import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatMessage } from "./ChatMessage";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { useChatMessages } from "@/hooks/chat/useChatMessages";
import { useModal } from "@/contexts/ModalContext";
import { useSettings } from "@/providers/SettingsProvider";

const TypingIndicator = () => (
    <div className="flex items-start gap-2 animate-fade-in">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm">
            <img src="/img/NewDiggerIcon.png" alt="" className="h-4 w-4 brightness-0 invert" />
        </div>
        <div className="flex items-center gap-1 rounded-sm rounded-bl-none border border-border bg-white/80 px-3 py-2.5 shadow-sm backdrop-blur-md dark:bg-gray-800/80">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60" />
        </div>
    </div>
);

export const ChatBot = ({ className = "" }: { className?: string }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { isAnyModalOpen } = useModal();
    const { aiDisabled } = useSettings();
    const {
        messages,
        isLoading,
        isFullScreen,
        sendMessage,
        clearChat,
        toggleFullScreen,
        handleEntityClick
    } = useChatMessages();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Scroll to bottom of messages
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Focus input when popover opens
    useEffect(() => {
        if ((isOpen || isFullScreen) && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen, isFullScreen]);

    if (aiDisabled) {
        return null;
    }

    const handleChatClick = (e: React.MouseEvent) => {
        const navigated = handleEntityClick(e);
        if (navigated) {
            setIsOpen(false); // Close chat after navigation
            if (isFullScreen) {
                toggleFullScreen(); // Close fullscreen mode after navigation
            }
        }
    };

    const closeChat = () => {
        setIsOpen(false);
        if (isFullScreen) {
            toggleFullScreen();
        }
    };

    // Render chat content
    const renderChatContent = () => (
        <div className={cn("flex flex-col", isFullScreen ? "h-full" : "h-[min(32rem,calc(100vh-8rem))]")}>
            <ChatHeader
                onClearChat={clearChat}
                onToggleFullScreen={toggleFullScreen}
                isFullScreen={isFullScreen}
                onClose={closeChat}
            />
            <ScrollArea className="min-h-0 flex-1 px-3 py-3">
                <div className={cn("space-y-3", isFullScreen && "mx-auto w-full max-w-3xl")} onClick={handleChatClick}>
                    {messages.map((message) => (
                        <ChatMessage
                            key={message.id}
                            message={message}
                            onEntityClick={handleEntityClick}
                        />
                    ))}
                    {isLoading && <TypingIndicator />}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>
            <ChatInput
                onSendMessage={sendMessage}
                isLoading={isLoading}
                inputRef={inputRef}
                centered={isFullScreen}
            />
        </div>
    );

    // If in fullscreen mode, render dialog instead of popover
    if (isFullScreen) {
        return (
            <Dialog open={isFullScreen} onOpenChange={(open) => !open && toggleFullScreen()}>
                <DialogContent className="flex h-[90vh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden rounded-sm p-0 [&>button]:hidden">
                    <DialogTitle className="sr-only">{t("chat.title")}</DialogTitle>
                    {renderChatContent()}
                </DialogContent>
            </Dialog>
        );
    }

    // Hide chat icon when any modal is open
    if (isAnyModalOpen) {
        return null;
    }

    return (
        <div className={cn("absolute bottom-6 right-6 z-50", className)}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        className="h-12 w-12 rounded-sm bg-gradient-to-br from-blue-500 to-blue-600 p-0 text-white shadow-lg ring-1 ring-white/20 transition-all duration-200 hover:scale-105 hover:shadow-xl hover:brightness-110 active:scale-95"
                        size="icon"
                        aria-label={isOpen ? t("chat.close") : t("chat.open")}
                    >
                        {isOpen ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <img src="/img/NewDiggerIcon.png" alt="" className="h-7 w-7 brightness-0 invert" />
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-sm border border-border bg-white/80 p-0 shadow-2xl backdrop-blur-lg dark:bg-black/60"
                    side="top"
                    align="end"
                    sideOffset={12}
                    collisionPadding={16}
                >
                    {renderChatContent()}
                </PopoverContent>
            </Popover>
        </div>
    );
};
